import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SPINNER_STYLE = `@keyframes ml-spin { to { transform: rotate(360deg); } }`;

// Read selected UI language directly from localStorage. We don't pull in
// LanguageContext here because MagicLogin renders before any auth/provider
// boundary and we want zero extra deps on this critical entry path.
const isTr = () => {
  try { return (localStorage.getItem("cop31_language") || "en") === "tr"; }
  catch { return false; }
};

export default function MagicLogin() {
  const [searchParams] = useSearchParams();
  const navigate        = useNavigate();
  const token           = searchParams.get("token");
  const tr              = isTr();

  const [status, setStatus] = useState("loading"); // loading | error | done
  const [error,  setError]  = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    // Spinner CSS enjekte et
    if (!document.getElementById("ml-styles")) {
      const s = document.createElement("style");
      s.id = "ml-styles";
      s.textContent = SPINNER_STYLE;
      document.head.appendChild(s);
    }

    if (calledRef.current) return;
    calledRef.current = true;

    if (!token) {
      setError(tr ? "Bu bağlantı geçersiz veya eksik." : "This link is invalid or missing.");
      setStatus("error");
      return;
    }

    axios
      .get(`${API}/auth/magic-login`, { params: { token } })
      .then((res) => {
        const data = res.data;
        // Tokeni ve kullanıcı bilgisini kaydet (normal girişle aynı format)
        localStorage.setItem("cop31_token", data.access_token);
        localStorage.setItem("cop31_user", JSON.stringify(data));

        const slug = data.tenant_slug || "";
        setStatus("done");
        // Kısa gecikme ile yönlendir
        setTimeout(() => {
          navigate(slug ? `/musteri-admin/${slug}/layout` : "/giris");
        }, 800);
      })
      .catch((err) => {
        // Phase A: if user already has a valid session in localStorage, the
        // expired magic-login token shouldn't matter — bounce them straight
        // to their layout page instead of dead-ending. The whole point of the
        // "Panelde İncele" button is to land on the layout, so honour that.
        const cachedToken = (() => {
          try { return localStorage.getItem("cop31_token") || ""; }
          catch { return ""; }
        })();
        const cachedUser  = (() => {
          try { return JSON.parse(localStorage.getItem("cop31_user") || "null") || {}; }
          catch { return {}; }
        })();
        const cachedSlug  = cachedUser.tenant_slug;
        if (cachedToken && cachedSlug) {
          setStatus("done");
          setTimeout(() => navigate(`/musteri-admin/${cachedSlug}/layout`), 200);
          return;
        }
        const msg =
          err.response?.data?.detail ||
          (tr ? "Bağlantı geçersiz veya süresi dolmuş olabilir." : "The link may be invalid or expired.");
        setError(msg);
        setStatus("error");
      });
  }, [token, navigate, tr]);

  if (status === "loading" || status === "done") {
    return (
      <div style={page}>
        <div style={card}>
          <div style={spinner} />
          <p style={hint}>
            {status === "done"
              ? (tr ? "Yönlendiriliyor…" : "Redirecting…")
              : (tr ? "Giriş yapılıyor…" : "Signing you in…")}
          </p>
        </div>
      </div>
    );
  }

  // Final error screen: same "logged-in fallback" applies — if user already
  // has a session, prefer routing them to their layout over showing a
  // dead-end "Invalid Link" page.
  let fallbackSlug = null;
  try {
    const cached = JSON.parse(localStorage.getItem("cop31_user") || "null");
    if (cached?.tenant_slug && localStorage.getItem("cop31_token")) {
      fallbackSlug = cached.tenant_slug;
    }
  } catch { /* ignore */ }

  return (
    <div style={page}>
      <div style={card}>
        <div style={iconCircle}>✕</div>
        <h2 style={title}>{tr ? "Bağlantı Geçersiz" : "Invalid Link"}</h2>
        <p style={sub}>{error}</p>
        {fallbackSlug ? (
          <button
            style={btnPrimary}
            data-testid="magic-login-fallback-layout"
            onClick={() => navigate(`/musteri-admin/${fallbackSlug}/layout`)}
          >
            {tr ? "Stand Planıma Git →" : "Go to My Layout →"}
          </button>
        ) : null}
        <button style={btn} onClick={() => navigate("/giris")}>
          {tr ? "Giriş Sayfasına Git" : "Go to Sign In"}
        </button>
      </div>
    </div>
  );
}

/* ── Styles ── */
const page  = { minHeight:"100vh", background:"linear-gradient(135deg,#eff6ff 0%,#f0fdf4 100%)",
                display:"flex", alignItems:"center", justifyContent:"center", padding:24,
                fontFamily:"Arial,sans-serif" };
const card  = { background:"#fff", borderRadius:20, boxShadow:"0 8px 40px rgba(0,0,0,.1)",
                padding:"48px 40px", maxWidth:420, width:"100%", textAlign:"center" };
const spinner={ width:48, height:48, border:"4px solid #e2e8f0", borderTop:"4px solid #1d4ed8",
                borderRadius:"50%", animation:"ml-spin .9s linear infinite", margin:"0 auto 20px" };
const hint  = { color:"#6b7280", fontSize:15 };
const iconCircle={ width:60, height:60, borderRadius:"50%", background:"#fef2f2", color:"#dc2626",
                   fontSize:26, fontWeight:700, display:"flex", alignItems:"center",
                   justifyContent:"center", margin:"0 auto 16px" };
const title = { fontSize:20, fontWeight:800, color:"#dc2626", margin:"8px 0" };
const sub   = { color:"#6b7280", fontSize:14, lineHeight:1.6, margin:"0 0 20px" };
const btn   = { background:"#f1f5f9", color:"#374151", border:"none", padding:"12px 32px",
                borderRadius:50, fontSize:14, fontWeight:600, cursor:"pointer", width:"100%" };
const btnPrimary = { background:"#1d4ed8", color:"#fff", border:"none", padding:"14px 36px",
                     borderRadius:50, fontSize:15, fontWeight:700, cursor:"pointer", width:"100%",
                     marginBottom:8 };
